import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { ISubCategory } from 'API/UniversalRequest/universalRequest';
import { getDisabilityConfirmationPdf } from 'API/disabilityConfirmation';
import { useIsDesktop, useIsOtpLogin } from 'Hooks';
import { AccordionArrowBlue, NewLeftArrow } from 'Images';
import { downloadFile } from 'Utils';
import { onEnterOrEscapeKeyDown } from 'Utils/accessibilityOnKeyDown';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemButton } from './ItemButton';
import { SUB_CATEGORY_OPTION_TYPE } from 'Enum/UniversalRequest';

const subCategoryOptionTypeColors: {
  [key in SUB_CATEGORY_OPTION_TYPE]: { color: string; bgcolor: string };
} = {
  [SUB_CATEGORY_OPTION_TYPE.personalArea]: {
    color: '#1552E8',
    bgcolor: '#1552E81C',
  },
  [SUB_CATEGORY_OPTION_TYPE.digitalForm]: {
    color: '#7C02D6',
    bgcolor: '#E3BEFF3B',
  },
  [SUB_CATEGORY_OPTION_TYPE.website]: {
    color: '#9D3200',
    bgcolor: '#FFD2BE40',
  },
};
interface ISubCategoriesListItem {
  subCategory: ISubCategory;
  handleSubCategoryClick: (id: number) => void;
}

export const SubCategoriesListItem = ({
  subCategory,
  handleSubCategoryClick,
}: ISubCategoriesListItem) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const { isOtp } = useIsOtpLogin();
  const isDesktop = useIsDesktop();

  const navigate = useNavigate();

  const onclick = async () => {
    if (subCategory.attachmentId) {
      setIsLoading(true);
      const sourceBase64 = await getDisabilityConfirmationPdf(
        subCategory.attachmentId,
      );

      if (sourceBase64?.file)
        downloadFile(
          sourceBase64.file,
          '.pdf',
          subCategory.disabilityName || '',
        );

      setIsLoading(false);
    } else if (!isOtp && subCategory.link) {
      subCategory.link.startsWith('https://')
        ? window.open(subCategory.link, '_blank')
        : navigate(subCategory.link);
    } else handleSubCategoryClick(subCategory.id);
  };

  return subCategory.options.length > 0 ? (
    <Accordion
      expanded={isExpanded}
      onChange={(e, expanded) => setIsExpanded(expanded)}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid #1552E81F',
        borderRadius: '8px !important',
        ':hover': {
          borderColor: isDesktop ? 'purple2.main' : undefined,
        },
      }}
    >
      <AccordionSummary
        sx={{
          p: '10px 16px',
        }}
        expandIcon={
          <Avatar
            src={AccordionArrowBlue}
            sx={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              bgcolor: '#E7EDFF',
            }}
            alt={''}
          />
        }
      >
        <Typography variant='body2' fontSize={'18px !important'}>
          {subCategory.title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          bgcolor: '#E7EDFF',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          p: '16px',
          borderRadius: '0 0 8px 8px',
        }}
      >
        {subCategory.options.map((option) => (
          <Link
            key={option.title}
            underline='none'
            href={
              option.type.id === SUB_CATEGORY_OPTION_TYPE.personalArea
                ? undefined
                : option.link
            }
            onClick={
              option.type.id === SUB_CATEGORY_OPTION_TYPE.personalArea
                ? () => navigate(option.link)
                : undefined
            }
            target={'_blank'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '6px',
              width: '100%',
              p: '16px',
              borderRadius: '8px',
              bgcolor: 'white',
              color: '#344266',
              border: '1px solid #1552E81F',
              ':hover': {
                borderColor: 'purple2.main',
              },
            }}
          >
            <Stack gap='6px'>
              <Typography
                sx={{
                  fontSize: '14px !important',
                  fontWeight: '400 !important',
                  lineHeight: '100% !important',
                  p: '3px 8px',
                  borderRadius: '12px',
                  backgroundColor:
                    subCategoryOptionTypeColors[option.type.id].bgcolor,
                  color: subCategoryOptionTypeColors[option.type.id].color,
                  width: 'fit-content',
                }}
              >
                {option.type.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: '18px !important',
                  fontWeight: '600 !important',
                  lineHeight: '100% !important',
                }}
              >
                {option.title}
              </Typography>
            </Stack>
            <Avatar
              src={NewLeftArrow}
              alt=''
              sx={{ width: '20px', height: '20px' }}
            />
          </Link>
        ))}
        <ItemButton
          isLoading={isLoading}
          onEnterOrEscapeKeyDown={onEnterOrEscapeKeyDown}
          onclick={onclick}
          subCategory={subCategory}
          isOptions={true}
        />
      </AccordionDetails>
    </Accordion>
  ) : (
    <ItemButton
      isLoading={isLoading}
      onEnterOrEscapeKeyDown={onEnterOrEscapeKeyDown}
      onclick={onclick}
      subCategory={subCategory}
      isOptions={false}
    />
  );
};
